import { useCallback, useState } from 'react';

const VIEW_WIDTH = 720;
const VIEW_HEIGHT = 260;
const MARGIN = { top: 20, right: 20, bottom: 36, left: 64 };
const Y_TICK_COUNT = 4;

/** @returns {boolean} True when at least one point has finite p10 and p90. */
export function hasRibbonData(series) {
  return (series || []).some(
    (pt) =>
      pt.p10 != null &&
      pt.p90 != null &&
      Number.isFinite(pt.p10) &&
      Number.isFinite(pt.p90)
  );
}

/**
 * Compact magnitude: 1.4k / 12.5M / 2.2B / 1.1T (no currency prefix).
 * Caps digit noise so axis labels stay short (~6 chars + suffix).
 */
export function formatCompactMagnitude(value) {
  if (value == null || !Number.isFinite(value)) return '—';
  const sign = value < 0 ? '−' : '';
  const abs = Math.abs(value);

  if (abs === 0) return '0';

  const tiers = [
    { div: 1e12, suffix: 'T' },
    { div: 1e9, suffix: 'B' },
    { div: 1e6, suffix: 'M' },
    { div: 1e3, suffix: 'k' },
  ];

  for (const { div, suffix } of tiers) {
    if (abs >= div) {
      const n = abs / div;
      const digits = n >= 100 ? 0 : n >= 10 ? 1 : 2;
      const raw = n
        .toFixed(digits)
        .replace(/\.0+$/, '')
        .replace(/(\.\d*[1-9])0+$/, '$1');
      return `${sign}${raw}${suffix}`;
    }
  }

  if (abs >= 100) return `${sign}${Math.round(abs)}`;
  if (abs >= 10) return `${sign}${abs.toFixed(1).replace(/\.0$/, '')}`;
  const small = abs.toFixed(abs < 1 ? 2 : 1).replace(/\.?0+$/, '');
  return `${sign}${small || '0'}`;
}

/** Money axis labels ($1.4k, $12M, $2.2B). */
export function formatCompactAxis(value) {
  if (value == null || !Number.isFinite(value)) return '—';
  if (value === 0) return '$0';
  const sign = value < 0 ? '−' : '';
  const body = formatCompactMagnitude(Math.abs(value));
  if (body === '—') return '—';
  return `${sign}$${body}`;
}

/** Count axis labels (logos) — same magnitude rules, no $. */
export function formatCountAxis(value) {
  return formatCompactMagnitude(value);
}

/** Chart x-axis: Now / M6 / 1y / 2y … (snap near whole years). */
export function formatXAxisLabel(label) {
  if (label == null) return '';
  if (label === 'Now') return 'Now';
  const match = /^M(\d+)$/.exec(String(label));
  if (!match) return String(label);
  const month = Number(match[1]);
  if (month < 12) return `M${month}`;
  const years = month / 12;
  const nearest = Math.round(years);
  // M59 on a 60-mo horizon → 5y, not 4.9y
  if (Math.abs(years - nearest) < 0.12) return `${nearest}y`;
  return `${years.toFixed(1).replace(/\.0$/, '')}y`;
}

/**
 * Nice tick step (Wilkinson-ish) so Y labels are round ($0, $50B) not $174706.1M.
 * @param {number} yMin
 * @param {number} yMax
 * @param {number} [tickCount]
 * @param {{ clampNonNegative?: boolean }} [opts] — keep domain ≥ 0 when series is non-negative
 * @returns {{ ticks: number[], min: number, max: number }}
 */
export function niceYTicks(yMin, yMax, tickCount = Y_TICK_COUNT, opts = {}) {
  let lo = Number.isFinite(yMin) ? yMin : 0;
  let hi = Number.isFinite(yMax) ? yMax : 1;
  if (opts.clampNonNegative) {
    lo = Math.max(0, lo);
    hi = Math.max(hi, lo + (Math.abs(hi) || 1) * 0.01);
  }
  const span = hi - lo;
  if (!Number.isFinite(span) || span === 0) {
    const pad = Math.abs(lo) * 0.1 || 1;
    const min = opts.clampNonNegative ? Math.max(0, lo - pad) : lo - pad;
    return { ticks: [min, lo, lo + pad], min, max: lo + pad };
  }

  const niceNum = (range, round) => {
    const exp = Math.floor(Math.log10(Math.abs(range)));
    const frac = range / 10 ** exp;
    let nice;
    if (round) {
      if (frac < 1.5) nice = 1;
      else if (frac < 3) nice = 2;
      else if (frac < 7) nice = 5;
      else nice = 10;
    } else if (frac <= 1) nice = 1;
    else if (frac <= 2) nice = 2;
    else if (frac <= 5) nice = 5;
    else nice = 10;
    return nice * 10 ** exp;
  };

  const maxTicks = Math.max(2, tickCount);
  const range = niceNum(span, false);
  const step = niceNum(range / maxTicks, true);
  let niceMin = Math.floor(lo / step) * step;
  const niceMax = Math.ceil(hi / step) * step;
  if (opts.clampNonNegative && niceMin < 0) niceMin = 0;
  const ticks = [];
  const steps = Math.round((niceMax - niceMin) / step);
  for (let i = 0; i <= steps; i += 1) {
    ticks.push(niceMin + step * i);
  }
  return { ticks, min: niceMin, max: niceMax };
}

/**
 * Y-axis domain with padding.
 * Ribbon (p10/p90) is excluded by default so explode upside does not crush the
 * selected-band line — ribbon may clip at the plot edge.
 *
 * @param {Array} series
 * @param {Array} [actuals]
 * @param {boolean|object} [ribbonOrOpts] — legacy `true` includes ribbon; prefer opts
 * @param {number} [referenceValue] — legacy 4th arg when ribbonOrOpts is boolean
 */
export function computeYExtent(series, actuals, ribbonOrOpts, referenceValue) {
  const opts =
    ribbonOrOpts && typeof ribbonOrOpts === 'object'
      ? ribbonOrOpts
      : {
          includeRibbon: Boolean(ribbonOrOpts),
          referenceValue,
        };
  const includeRibbon = Boolean(opts.includeRibbon);
  const ref = opts.referenceValue;

  const values = [];
  for (const pt of series || []) {
    if (pt.value != null && Number.isFinite(pt.value)) values.push(pt.value);
    if (includeRibbon) {
      if (pt.p10 != null && Number.isFinite(pt.p10)) values.push(pt.p10);
      if (pt.p90 != null && Number.isFinite(pt.p90)) values.push(pt.p90);
    }
  }
  for (const pt of actuals || []) {
    if (pt.value != null && Number.isFinite(pt.value)) values.push(pt.value);
  }
  if (ref != null && Number.isFinite(ref)) {
    values.push(ref);
  }
  if (values.length === 0) return { min: 0, max: 1, nonNegative: true };

  let min = Math.min(...values);
  let max = Math.max(...values);
  const dataMin = min;
  if (min > 0 && min / max < 0.35) min = 0;
  if (max < 0 && max / min < 0.35) max = 0;
  if (min === max) {
    const pad = min === 0 ? 1 : Math.abs(min) * 0.1 || 1;
    const lo = min - pad;
    return {
      min: dataMin >= 0 ? Math.max(0, lo) : lo,
      max: max + pad,
      nonNegative: dataMin >= 0,
    };
  }
  const pad = (max - min) * 0.08;
  let lo = min - pad;
  if (dataMin >= 0) lo = Math.max(0, lo);
  return { min: lo, max: max + pad, nonNegative: dataMin >= 0 };
}

function innerSize() {
  return {
    width: VIEW_WIDTH - MARGIN.left - MARGIN.right,
    height: VIEW_HEIGHT - MARGIN.top - MARGIN.bottom,
  };
}

function xAt(index, count, innerWidth) {
  if (count <= 1) return MARGIN.left + innerWidth / 2;
  return MARGIN.left + (index / (count - 1)) * innerWidth;
}

function yAt(value, yMin, yMax, innerHeight) {
  const span = yMax - yMin || 1;
  const ratio = (value - yMin) / span;
  return MARGIN.top + innerHeight * (1 - ratio);
}

/** Clamp Y into the plot so clipped ribbon stays inside the card. */
function clampPlotY(y, innerHeight) {
  const top = MARGIN.top;
  const bottom = MARGIN.top + innerHeight;
  return Math.min(bottom, Math.max(top, y));
}

function buildLinePath(points) {
  if (points.length === 0) return '';
  return points
    .map((p, i) => `${i === 0 ? 'M' : 'L'} ${p.x.toFixed(2)} ${p.y.toFixed(2)}`)
    .join(' ');
}

function buildRibbonPath(upper, lower) {
  if (upper.length === 0) return '';
  const forward = upper
    .map((p, i) => `${i === 0 ? 'M' : 'L'} ${p.x.toFixed(2)} ${p.y.toFixed(2)}`)
    .join(' ');
  const backward = [...lower]
    .reverse()
    .map((p) => `L ${p.x.toFixed(2)} ${p.y.toFixed(2)}`)
    .join(' ');
  return `${forward} ${backward} Z`;
}

/** Prefer yearly ticks on long horizons; keep ≤7 labels. */
export function xLabelIndices(count) {
  if (count <= 1) return [0];
  if (count <= 8) return Array.from({ length: count }, (_, i) => i);

  const last = count - 1;
  const yearStep = count > 72 ? 24 : 12;
  const yearly = [0];
  for (let i = yearStep; i < last; i += yearStep) yearly.push(i);
  if (yearly[yearly.length - 1] !== last) yearly.push(last);

  if (yearly.length <= 7) return yearly;

  const step = Math.ceil(last / 6);
  const sparse = [0];
  for (let i = step; i < last; i += step) sparse.push(i);
  if (sparse[sparse.length - 1] !== last) sparse.push(last);
  return sparse;
}

/** Endpoints only — dense monthly dots clutter long horizons. */
function markerIndices(count) {
  if (count <= 1) return [0];
  return [0, count - 1];
}

function resolveActualPoints(
  series,
  actuals,
  yMin,
  yMax,
  innerWidth,
  innerHeight,
  domainLabels = series.map((pt) => pt.label),
  domainCount = domainLabels.length
) {
  const labelToIndex = new Map(series.map((pt, i) => [pt.label, i]));
  const domainLabelToIndex = new Map(domainLabels.map((label, i) => [label, i]));
  const points = [];
  for (const pt of actuals || []) {
    const index = Number.isFinite(pt.index)
      ? pt.index
      : domainLabelToIndex.has(pt.label)
        ? domainLabelToIndex.get(pt.label)
        : labelToIndex.get(pt.label);
    if (index == null || pt.value == null || !Number.isFinite(pt.value)) continue;
    points.push({
      label: pt.label,
      x: xAt(index, domainCount, innerWidth),
      y: yAt(pt.value, yMin, yMax, innerHeight),
    });
  }
  return points;
}

/**
 * Forecast line chart — pure SVG, admin dark theme.
 *
 * @param {object} props
 * @param {Array<{ label: string, value: number, p10?: number, p90?: number }>} props.series
 * @param {boolean} [props.ribbon] — fill envelope between p10/p90 when present
 * @param {Array<{ label: string, value: number }>} [props.actuals] — dashed overlay + dots
 * @param {number} [props.referenceValue] — horizontal guide (e.g. opening cash)
 * @param {string} [props.referenceLabel]
 * @param {string} [props.title]
 * @param {string} [props.emptyMessage]
 * @param {(value: number) => string} [props.formatValue]
 * @param {Array<{ label: string, value: number }>} [props.series[].tooltipRows]
 * @param {string[]} [props.xLabels] — optional full domain labels when actuals precede forecast
 * @param {number} [props.seriesStartIndex] — forecast start within xLabels
 */
export default function ForecastLineChart({
  series,
  ribbon = false,
  actuals,
  referenceValue,
  referenceLabel = 'Opening',
  title,
  emptyMessage = 'No chart data.',
  formatValue = formatCompactAxis,
  xLabels,
  seriesStartIndex = 0,
}) {
  const [hoverTarget, setHoverTarget] = useState(null);
  const rows = series || [];
  const domainLabels = xLabels?.length ? xLabels : rows.map((pt) => pt.label);
  const domainCount = domainLabels.length;

  const showRibbon = rows.length > 0 && ribbon && hasRibbonData(rows);
  const rawExtent = computeYExtent(rows, actuals, {
    includeRibbon: false,
    referenceValue,
  });
  const { ticks, min: yMin, max: yMax } = niceYTicks(
    rawExtent.min,
    rawExtent.max,
    Y_TICK_COUNT,
    { clampNonNegative: rawExtent.nonNegative }
  );
  const { width: innerWidth, height: innerHeight } = innerSize();
  const count = domainCount;

  const mainPoints = rows.map((pt, i) => ({
    label: pt.label,
    x: xAt(i + seriesStartIndex, count, innerWidth),
    y: yAt(pt.value ?? 0, yMin, yMax, innerHeight),
  }));

  const ribbonUpper = showRibbon
    ? rows.map((pt, i) => ({
        x: xAt(i + seriesStartIndex, count, innerWidth),
        y: clampPlotY(yAt(pt.p90, yMin, yMax, innerHeight), innerHeight),
      }))
    : [];
  const ribbonLower = showRibbon
    ? rows.map((pt, i) => ({
        x: xAt(i + seriesStartIndex, count, innerWidth),
        y: clampPlotY(yAt(pt.p10, yMin, yMax, innerHeight), innerHeight),
      }))
    : [];

  const actualPoints = resolveActualPoints(
    rows,
    actuals,
    yMin,
    yMax,
    innerWidth,
    innerHeight,
    domainLabels,
    domainCount
  );
  const xIndices = xLabelIndices(domainCount);
  const markers = markerIndices(rows.length);
  const showReference =
    referenceValue != null && Number.isFinite(referenceValue);
  const referenceY = showReference
    ? yAt(referenceValue, yMin, yMax, innerHeight)
    : null;
  const showZero =
    yMin < 0 && yMax > 0 ? yAt(0, yMin, yMax, innerHeight) : null;

  const chartLabel = title ? `${title} line chart` : 'Forecast line chart';
  const hoverForecast =
    hoverTarget?.type === 'forecast' ? rows[hoverTarget.index] : null;
  const hoverActual =
    hoverTarget?.type === 'actual' ? hoverTarget.point : null;
  const hoverPoint =
    hoverTarget?.type === 'forecast'
      ? mainPoints[hoverTarget.index]
      : hoverActual;
  const hoverTooltipRows = hoverForecast?.tooltipRows || [];

  const handleMove = useCallback(
    (event) => {
      if (!mainPoints.length) return;
      const svg = event.currentTarget;
      const rect = svg.getBoundingClientRect();
      if (!rect.width) return;
      const x = ((event.clientX - rect.left) / rect.width) * VIEW_WIDTH;
      if (x < MARGIN.left || x > VIEW_WIDTH - MARGIN.right) {
        setHoverTarget(null);
        return;
      }
      const candidates = [
        ...mainPoints.map((point, index) => ({
          type: 'forecast',
          index,
          x: point.x,
        })),
        ...actualPoints.map((point) => ({
          type: 'actual',
          point,
          x: point.x,
        })),
      ];
      let best = candidates[0];
      let bestDist = Infinity;
      for (const candidate of candidates) {
        const d = Math.abs(candidate.x - x);
        if (d < bestDist) {
          bestDist = d;
          best = candidate;
        }
      }
      setHoverTarget(best);
    },
    [actualPoints, mainPoints]
  );

  const handleLeave = useCallback(() => setHoverTarget(null), []);

  if (rows.length === 0) {
    return <p className="chart-empty">{emptyMessage}</p>;
  }

  const tooltipLeftPct = hoverPoint
    ? Math.min(92, Math.max(8, (hoverPoint.x / VIEW_WIDTH) * 100))
    : 0;

  return (
    <div className="forecast-line-chart">
      {title ? <h3 className="forecast-line-chart__title">{title}</h3> : null}
      <div className="forecast-line-chart__plot">
        <svg
          className="forecast-line-chart__svg"
          viewBox={`0 0 ${VIEW_WIDTH} ${VIEW_HEIGHT}`}
          role="img"
          aria-label={chartLabel}
          onMouseMove={handleMove}
          onMouseLeave={handleLeave}
        >
          {ticks.map((tick, tickIndex) => {
            const y = yAt(tick, yMin, yMax, innerHeight);
            return (
              <g key={`tick-${tickIndex}-${tick}`}>
                <line
                  className="forecast-line-chart__grid"
                  x1={MARGIN.left}
                  y1={y}
                  x2={VIEW_WIDTH - MARGIN.right}
                  y2={y}
                />
                <text
                  className="forecast-line-chart__axis-label forecast-line-chart__axis-label--y"
                  x={MARGIN.left - 8}
                  y={y}
                  textAnchor="end"
                  dominantBaseline="middle"
                >
                  {formatValue(tick)}
                </text>
              </g>
            );
          })}

          {showZero != null ? (
            <line
              className="forecast-line-chart__zero"
              x1={MARGIN.left}
              y1={showZero}
              x2={VIEW_WIDTH - MARGIN.right}
              y2={showZero}
            />
          ) : null}

          {showReference ? (
            <g>
              <line
                className="forecast-line-chart__reference"
                x1={MARGIN.left}
                y1={referenceY}
                x2={VIEW_WIDTH - MARGIN.right}
                y2={referenceY}
              />
              <text
                className="forecast-line-chart__reference-label"
                x={VIEW_WIDTH - MARGIN.right}
                y={referenceY - 6}
                textAnchor="end"
              >
                {referenceLabel}
              </text>
            </g>
          ) : null}

          {showRibbon ? (
            <path
              className="forecast-line-chart__ribbon"
              d={buildRibbonPath(ribbonUpper, ribbonLower)}
            />
          ) : null}

          <path className="forecast-line-chart__line" d={buildLinePath(mainPoints)} />

          {actualPoints.length > 1 ? (
            <path
              className="forecast-line-chart__actuals-line"
              d={buildLinePath(actualPoints)}
            />
          ) : null}

          {actualPoints.map((pt) => (
            <circle
              key={`actual-${pt.label}`}
              className="forecast-line-chart__actuals-dot"
              cx={pt.x}
              cy={pt.y}
              r={2.25}
            />
          ))}

          {markers.map((i) => (
            <circle
              key={`point-${rows[i].label}`}
              className="forecast-line-chart__point"
              cx={mainPoints[i].x}
              cy={mainPoints[i].y}
              r={2.25}
            />
          ))}

          {hoverPoint ? (
            <g className="forecast-line-chart__hover" pointerEvents="none">
              <line
                className="forecast-line-chart__hover-rule"
                x1={hoverPoint.x}
                y1={MARGIN.top}
                x2={hoverPoint.x}
                y2={MARGIN.top + innerHeight}
              />
              <circle
                className="forecast-line-chart__hover-dot"
                cx={hoverPoint.x}
                cy={hoverPoint.y}
                r={3}
              />
            </g>
          ) : null}

          {xIndices.map((i) => (
            <text
              key={`x-${domainLabels[i]}`}
              className="forecast-line-chart__axis-label forecast-line-chart__axis-label--x"
              x={xAt(i, count, innerWidth)}
              y={VIEW_HEIGHT - 10}
              textAnchor="middle"
            >
              {formatXAxisLabel(domainLabels[i])}
            </text>
          ))}
        </svg>

        {(hoverForecast || hoverActual) && hoverPoint ? (
          <div
            className="forecast-line-chart__tooltip"
            style={{ left: `${tooltipLeftPct}%` }}
            role="status"
          >
            <div className="forecast-line-chart__tooltip-time">
              {formatXAxisLabel((hoverForecast || hoverActual).label)}
              {(hoverForecast || hoverActual).label !==
              formatXAxisLabel((hoverForecast || hoverActual).label)
                ? ` · ${(hoverForecast || hoverActual).label}`
                : ''}
            </div>
            <div className="forecast-line-chart__tooltip-row">
              <span>{hoverActual ? 'Actual' : 'Value'}</span>
              <strong>{formatValue((hoverForecast || hoverActual).value)}</strong>
            </div>
            {hoverForecast &&
              hoverTooltipRows.map((row) => (
                <div className="forecast-line-chart__tooltip-row" key={row.label}>
                  <span>{row.label}</span>
                  <strong>{formatValue(row.value)}</strong>
                </div>
              ))}
            {hoverForecast && showRibbon && hoverForecast.p10 != null && hoverForecast.p90 != null ? (
              <>
                <div className="forecast-line-chart__tooltip-row">
                  <span>P10</span>
                  <strong>{formatValue(hoverForecast.p10)}</strong>
                </div>
                <div className="forecast-line-chart__tooltip-row">
                  <span>P90</span>
                  <strong>{formatValue(hoverForecast.p90)}</strong>
                </div>
              </>
            ) : null}
          </div>
        ) : null}
      </div>

      {(showRibbon || actualPoints.length > 0 || showReference) && (
        <div className="forecast-line-chart__legend" aria-hidden="true">
          <span className="forecast-line-chart__legend-item">
            <span className="forecast-line-chart__legend-swatch forecast-line-chart__legend-swatch--forecast" />
            Forecast
          </span>
          {showRibbon ? (
            <span className="forecast-line-chart__legend-item">
              <span className="forecast-line-chart__legend-swatch forecast-line-chart__legend-swatch--ribbon" />
              P10–P90 (clipped to band scale)
            </span>
          ) : null}
          {actualPoints.length > 0 ? (
            <span className="forecast-line-chart__legend-item">
              <span className="forecast-line-chart__legend-swatch forecast-line-chart__legend-swatch--actuals" />
              Actuals
            </span>
          ) : null}
          {showReference ? (
            <span className="forecast-line-chart__legend-item">
              <span className="forecast-line-chart__legend-swatch forecast-line-chart__legend-swatch--reference" />
              {referenceLabel}
            </span>
          ) : null}
        </div>
      )}
    </div>
  );
}
